import type { LocalService } from "@/data/local-services";
import type { Location } from "@/data/locations";
import { cityMeta, tierOf, type Tier } from "@/data/location-meta";
import { isFocusLocation, nameVariants } from "@/data/focus";

// Deterministischer kleiner Hash zur Auswahl von Text-Varianten je (Service, Ort)
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function pick<T>(arr: T[], key: string, salt = ""): T {
  return arr[hash(key + "|" + salt) % arr.length];
}

// Hilfsfunktion: 2–3 Varianten deterministisch ohne Doppelung
function pickN<T>(arr: T[], key: string, n: number): T[] {
  const out: T[] = [];
  const start = hash(key) % arr.length;
  for (let i = 0; out.length < n && i < arr.length; i++) {
    const v = arr[(start + i) % arr.length];
    if (!out.includes(v)) out.push(v);
  }
  return out;
}

const distanzWien: Record<string, string> = {
  niederoesterreich: "direkt vor den Toren Wiens",
  burgenland: "rund 60 km östlich von Wien",
  oberoesterreich: "in rund 2 Stunden von Wien aus erreichbar",
  steiermark: "rund 200 km südlich von Wien",
  kaernten: "im Süden Österreichs",
  tirol: "im Westen Österreichs",
  "salzburg-land": "rund 300 km westlich von Wien",
  vorarlberg: "im äußersten Westen Österreichs",
  wien: "im Herzen Wiens",
};

export type PriceRow = { leistung: string; richtwert: string; hinweis?: string };

export type GeneratedContent = {
  title: string;
  h1: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  intro: string;
  introExtra: string; // 2. Absatz
  localContext: string;
  neighborhoodSection?: string; // Stadtteile-Absatz
  localTips: string[]; // 3-6 lokale Tipps
  exampleCases: { title: string; text: string }[]; // 2-3 Fallbeispiele
  priceTable: PriceRow[];
  priceNote: string;
  whyUs: string[];
  faq: { question: string; answer: string }[];
  tier: Tier;
  noindex: boolean;
};

// Basispreise (Richtwerte) pro Service – werden mit lokalem Faktor multipliziert
const basePriceTable: Record<string, PriceRow[]> = {
  entruempelung: [
    { leistung: "Kleinwohnung bis 40 m²", richtwert: "ab 590 €" },
    { leistung: "2-Zimmer-Wohnung bis 60 m²", richtwert: "ab 890 €" },
    { leistung: "3-Zimmer-Wohnung bis 90 m²", richtwert: "ab 1.290 €" },
    { leistung: "Einfamilienhaus inkl. Keller", richtwert: "ab 2.490 €" },
    { leistung: "Keller, Dachboden oder Garage einzeln", richtwert: "ab 290 €" },
    { leistung: "Gut erhaltenes Inventar", richtwert: "faire Preisgestaltung" },
  ],
  "messi-wohnung": [
    { leistung: "Erstbegehung & Konzept (vor Ort)", richtwert: "kostenlos" },
    { leistung: "Messie-Räumung bis 50 m²", richtwert: "ab 1.490 €" },
    { leistung: "Messie-Räumung 50–80 m²", richtwert: "ab 2.490 €" },
    { leistung: "Messie-Räumung 80–120 m²", richtwert: "ab 3.490 €" },
    { leistung: "Schädlingsbekämpfung (Kammerjäger)", richtwert: "ab 290 €" },
    { leistung: "Grundreinigung & Geruchsneutralisation", richtwert: "ab 490 €" },
  ],
  umzug: [
    { leistung: "1-Zimmer-Wohnung lokal", richtwert: "ab 490 €" },
    { leistung: "2-Zimmer-Wohnung lokal", richtwert: "ab 790 €" },
    { leistung: "3-Zimmer-Wohnung lokal", richtwert: "ab 1.190 €" },
    { leistung: "4-Zimmer-Wohnung oder Einfamilienhaus", richtwert: "ab 1.690 €" },
    { leistung: "Möbellift (pro Tag, falls nötig)", richtwert: "ab 290 €" },
    { leistung: "Halteverbot-Beantragung", richtwert: "ab 90 €" },
    { leistung: "Klaviertransport (Pianino)", richtwert: "ab 190 €" },
  ],
  transport: [
    { leistung: "Kleintransport bis 3,5 t / lokal", richtwert: "ab 89 € / Stunde" },
    { leistung: "Möbeltransport mit 2 Mann", richtwert: "ab 129 € / Stunde" },
    { leistung: "Einzelstück / IKEA-Abholung", richtwert: "ab 149 € pauschal" },
    { leistung: "Sperrgut (Sofa, Schrank)", richtwert: "ab 99 € pro Stück" },
    { leistung: "Anfahrt im Einsatzgebiet", richtwert: "im Preis enthalten" },
    { leistung: "Klavier oder Tresor", richtwert: "auf Anfrage" },
  ],
  wohnungsaufloesung: [
    { leistung: "1-Zimmer-Wohnung komplett", richtwert: "ab 690 €" },
    { leistung: "2-Zimmer-Wohnung komplett", richtwert: "ab 990 €" },
    { leistung: "3-Zimmer-Wohnung komplett", richtwert: "ab 1.490 €" },
    { leistung: "Einfamilienhaus komplett", richtwert: "ab 2.890 €" },
    { leistung: "Endreinigung (besenrein hinaus)", richtwert: "ab 290 €" },
    { leistung: "Gut erhaltenes Inventar", richtwert: "faire Preisgestaltung" },
  ],
  firmenumzug: [
    { leistung: "Kleinbetrieb bis 5 Mitarbeiter", richtwert: "ab 1.890 €" },
    { leistung: "Mittelbetrieb 5–20 Mitarbeiter", richtwert: "ab 3.490 €" },
    { leistung: "Großbetrieb / Produktion", richtwert: "Projektpreis nach Begehung" },
    { leistung: "Wochenend-/Nachtzuschlag", richtwert: "+ 20 %" },
    { leistung: "Maschinen- / Regaldemontage", richtwert: "ab 90 € / Stunde / Person" },
    { leistung: "Abtransport Altequipment", richtwert: "ab 190 €" },
  ],
  bueroumzug: [
    { leistung: "Pro Arbeitsplatz", richtwert: "ab 149 €" },
    { leistung: "Besprechungsraum komplett", richtwert: "ab 490 €" },
    { leistung: "Server-/IT-Raum (vorsichtig verpackt)", richtwert: "ab 690 €" },
    { leistung: "Aktentransport DSGVO-konform", richtwert: "ab 49 € / m" },
    { leistung: "Etiketten- und Beschriftungssystem", richtwert: "kostenlos inkludiert" },
    { leistung: "Wochenendabwicklung", richtwert: "+ 20 %" },
  ],
};

function adjustedPriceTable(serviceSlug: string, faktor: number): PriceRow[] {
  const base = basePriceTable[serviceSlug] ?? [];
  if (Math.abs(faktor - 1.0) < 0.01) return base;
  return base.map((r) => {
    // adjustiere "ab XXX €" Beträge
    const match = r.richtwert.match(/^ab ([\d.]+)\s*€(.*)$/);
    if (!match) return r;
    const value = parseInt(match[1].replace(".", ""), 10);
    if (!Number.isFinite(value)) return r;
    const adjusted = Math.round((value * faktor) / 10) * 10;
    const formatted = adjusted.toLocaleString("de-AT");
    return { ...r, richtwert: `ab ${formatted} €${match[2]}` };
  });
}

// Prio-Städte für Entrümpelungs-Fokus – hier maximaler exact-match Keyword-Push
// und Tier-1-Behandlung (mehr Content, mehr FAQ, mehr Beispiele).
const entruempelungPrio = new Set<string>([
  "wien",
  "wiener-neustadt",
  "st-poelten",
  "baden",
  "eisenstadt",
  "mattersburg",
  // alle 23 Wiener Bezirke bekommen den Entrümpelungs-Boost ebenfalls
  "wien-1-innere-stadt", "wien-2-leopoldstadt", "wien-3-landstrasse",
  "wien-4-wieden", "wien-5-margareten", "wien-6-mariahilf",
  "wien-7-neubau", "wien-8-josefstadt", "wien-9-alsergrund",
  "wien-10-favoriten", "wien-11-simmering", "wien-12-meidling",
  "wien-13-hietzing", "wien-14-penzing", "wien-15-rudolfsheim-fuenfhaus",
  "wien-16-ottakring", "wien-17-hernals", "wien-18-waehring",
  "wien-19-doebling", "wien-20-brigittenau", "wien-21-floridsdorf",
  "wien-22-donaustadt", "wien-23-liesing",
]);

export function generateLocalContent(
  service: LocalService,
  location: Location,
): GeneratedContent {
  const key = `${service.slug}-${location.slug}`;
  let tier = tierOf(location.slug);
  const meta = cityMeta[location.slug];

  const isEntruempelungPrio =
    service.slug === "entruempelung" && entruempelungPrio.has(location.slug);
  if (isEntruempelungPrio) tier = 1; // Boost: mehr Content, mehr FAQ, mehr Tipps

  const ortIn =
    location.type === "wien-bezirk"
      ? `im ${location.name.replace(/^Wien /, "")}`
      : location.type === "bundesland"
        ? `in ${location.name === "Wien" ? "Wien" : location.name}`
        : `in ${location.name}`;

  // Bare-Name für exact-match Keyword (z. B. "Entrümpelung Salzburg")
  const ortBare =
    location.type === "wien-bezirk"
      ? location.name.replace(/^Wien /, "")
      : location.name;

  const bundesland = location.bundesland;
  const plzInfo = location.plz ? ` (PLZ ${location.plz})` : "";

  // === Title / Meta / OG === (Exact-Match-Keyword vorne)
  const regionTag = location.region ? ` (${location.region})` : "";
  const titleVariants = isEntruempelungPrio
    ? [
        `Entrümpelung ${ortBare} – Angebot ab 290 € | Schober`,
        `Entrümpelung ${ortBare}: Angebot, versichert | Schober`,
        `Entrümpelung ${ortBare} – Termin in 48 h | Schober`,
        `Profi-Entrümpelung ${ortBare} – Angebot | Schober`,
        `Entrümpelung ${ortBare}${plzInfo} – ab 290 € | Schober`,
      ]
    : [
        `${service.title} ${ortBare} – Angebot | Schober Transportservice`,
        `${service.title} ${ortBare}: Angebot & versichert | Schober`,
        `${service.titleLong} ${ortBare} | Schober Transportservice`,
        `Profi-${service.title} ${ortBare} – Angebot | Schober`,
        `${service.title} ${ortBare}${plzInfo} – versichert | Schober`,
        `${service.title} ${ortBare} – kostenlose Besichtigung | Schober`,
        `${service.title} ${ortBare}${regionTag} – Angebot | Schober`,
      ];

  const metaVariants = isEntruempelungPrio
    ? [
        `Entrümpelung ${ortBare}${plzInfo}: Angebot ab 290 €, versichert bis 25.000 €, Termin in 48 h. Faire Preisgestaltung, Abtransport des Räumguts, besenreine Übergabe. ☎ +43 680 230 87 00.`,
        `Entrümpelung ${ortBare} mit klarem Angebot: Keller, Wohnung, Haus – schnell, sauber, diskret. Kostenlose Besichtigung, Angebot in 24 h. ☎ +43 680 230 87 00.`,
        `Professionelle Entrümpelung ${ortBare}${plzInfo}: eigenes Team, keine Subunternehmer, faire Preisgestaltung bei gut erhaltenem Inventar. Jetzt Angebot anfordern.`,
        `Entrümpelung ${ortBare}: ab 290 € für Keller, ab 590 € für Kleinwohnung. Versichert, Angebot, Termin in 48 h. Schober – Ihr Partner in ${bundesland}.`,
        `Entrümpelung ${ortBare}${regionTag}: Preistransparenz, kostenlose Besichtigung, faire Preisgestaltung. Schober räumt schnell und diskret. ☎ 0680 230 87 00.`,
      ]
    : [
        `${service.title} ${ortBare}${plzInfo}: Angebot, versichert bis 25.000 €, Termin oft in 48 h. Schober Transportservice räumt, transportiert und übersiedelt zuverlässig. ☎ +43 680 230 87 00.`,
        `Professionelle ${service.title} ${ortBare}${plzInfo} mit verbindlichem Angebot. Eigenes Team, Abtransport des Räumguts, faire Preisgestaltung. Jetzt kostenloses Angebot in 24 h anfordern.`,
        `${service.titleLong} ${ortBare}${plzInfo}: erfahrenes Team, faire Preise, diskrete Abwicklung. Schober Transportservice – Ihr Partner in ${bundesland}. ☎ +43 680 230 87 00.`,
        `${service.title} ${ortBare}: kostenlose Besichtigung, Preistransparenz, eigene Mannschaft. Schober Transportservice arbeitet seit über 10 Jahren in ${bundesland}.`,
        `${service.title} ${ortBare}${regionTag}: schnelle Termine, faire Angebote, eigenes geschultes Team. Schriftliches Angebot in 24 h. ☎ +43 680 230 87 00.`,
      ];

  const h1Variants = isEntruempelungPrio
    ? [
        `Entrümpelung ${ortBare} – Angebot, versichert, Termin in 48 h`,
        `Entrümpelung ${ortBare}: Ihr Profi mit verbindlichem Angebot`,
        `Entrümpelung ${ortBare} – schnell, sauber, fair kalkuliert`,
        `Professionelle Entrümpelung ${ortBare}${regionTag}`,
      ]
    : [
        `${service.titleLong} ${ortBare}`,
        `${service.title} ${ortBare} – schnell, sauber, transparent kalkuliert`,
        `${service.title} ${ortBare}: Ihr Profi-Service mit Angebot`,
        `${service.title} ${ortBare} – versichert, diskret, ohne Subunternehmer`,
        `${service.titleLong} ${ortBare}${regionTag}`,
      ];

  // === Intro (2 Absätze) ===
  const introVariants = [
    `${service.intro} ${ortIn}${plzInfo} sind wir besonders schnell vor Ort – meist innerhalb von 48 Stunden mit Besichtigung oder Sofort-Angebot.`,
    `Sie suchen einen verlässlichen Partner für ${service.noun} ${ortIn}? ${service.intro} Für Auftraggeber ${ortIn}${plzInfo} bieten wir kurze Anfahrtswege, faire Konditionen und persönliche Beratung.`,
    `${service.noun} ${ortIn}${plzInfo} – darauf sind wir spezialisiert. ${service.intro}`,
    `Für eine professionelle ${service.noun} ${ortIn}${plzInfo} ist Schober Transportservice der richtige Ansprechpartner. ${service.intro}`,
    `Ob Wohnung, Haus oder Gewerbeobjekt: Bei ${service.noun} ${ortIn}${plzInfo} liefern wir verlässlich. ${service.intro}`,
  ];

  const introExtraVariants = [
    `Wir arbeiten ${ortIn} mit eigenem geschultem Team und eigenem Fuhrpark – ohne Subunternehmer. Das bedeutet für Sie: ein einziger Ansprechpartner von der Besichtigung bis zur Schlüsselübergabe, ein klar kalkuliertes Angebot und volle Versicherung bis 25.000 € pro Auftrag. Wir kennen die örtliche Hausverwaltung-Praxis ebenso wie die regionalen Gegebenheiten und sorgen dafür, dass Ihr Auftrag termingerecht und rückstandsfrei abgewickelt wird.`,
    `Was Schober Transportservice ${ortIn} ausmacht: Wir kalkulieren transparent vor Ort, dokumentieren den Auftrag fotografisch und übergeben am Ende besenrein an Sie oder Ihre Hausverwaltung. Bei gut erhaltenem Inventar gestalten wir das Angebot Ihrer ${service.noun} entsprechend fair. Das übrige Räumgut transportieren wir ab und übergeben es an befugte Partnerbetriebe.`,
    `Unsere Aufträge ${ortIn} betreuen wir vom ersten Anruf bis zur finalen Schlüsselübergabe persönlich. Sie erhalten innerhalb von 24 Stunden ein detailliertes, schriftliches Angebot ohne versteckte Kosten. Termine sind oft binnen 48 Stunden möglich, in Notfällen – etwa bei kurzfristiger Wohnungsübergabe oder im Trauerfall – arbeiten wir auch am gleichen Tag. Unsere Mannschaft ist geschult und arbeitet für die weitere Behandlung des Räumguts ausschließlich mit befugten Partnerbetrieben in ${bundesland} zusammen.`,
    `Schober Transportservice ist ${ortIn} seit über zehn Jahren ein verlässlicher Partner für ${service.noun}. Wir kombinieren routinierte Abläufe mit individueller Betreuung: kein anonymes Call-Center, sondern feste Ansprechpartner, die Ihr Objekt, die Hausverwaltung und die regionalen Gegebenheiten in ${bundesland} kennen. Sie erhalten ein detailliertes Angebot, einen verbindlichen Zeitplan und am Ende eine Foto-Dokumentation des Endzustands für Ihre Unterlagen.`,
  ];

  // Entrümpelung-Prio: exact-match Intros vorne – exakt für Google-Ranking auf "Entrümpelung <Ort>"
  if (isEntruempelungPrio) {
    introVariants.unshift(
      `Entrümpelung ${ortBare}${plzInfo} zum Angebot: Wir räumen Keller, Wohnung, Haus oder Gewerbeobjekt komplett – schnell, sauber und ohne versteckte Kosten. Termin oft binnen 48 Stunden, faire Preisgestaltung bei gut erhaltenem Inventar.`,
      `Sie brauchen eine Entrümpelung ${ortIn}? Schober Transportservice räumt Ihre Immobilie besenrein, transportiert das Räumgut ab und rechnet gut erhaltene Möbel, Antiquitäten oder Elektrogeräte fair auf den Preis an – oft 10–30 % Ersparnis.`,
      `Professionelle Entrümpelung ${ortBare}: Vom einzelnen Kellerabteil (ab 290 €) bis zum kompletten Einfamilienhaus (ab 2.490 €) übernehmen wir alles – mit Angebot, Versicherung bis 25.000 € und persönlichem Ansprechpartner.`,
    );
    introExtraVariants.unshift(
      `Warum Kunden ${ortIn} Schober für die Entrümpelung wählen: Wir kommen mit eigenem Team und eigenem Fuhrpark, kalkulieren nach kostenloser Besichtigung einen verbindlichen Angebot und dokumentieren jeden Schritt fotografisch. Heikle Stücke wie Farben, Batterien oder Elektronik verladen wir getrennt, und bei gut erhaltenem Inventar kalkulieren wir Ihre Entrümpelungs-Kosten ${ortIn} entsprechend fair.`,
      `Ablauf einer Entrümpelung ${ortIn}: Anruf oder Online-Anfrage, kostenlose Besichtigung binnen 48 h, schriftliches Angebot in 24 h, Räumung im vereinbarten Zeitfenster, besenreine Übergabe an Sie oder die Hausverwaltung – inklusive Foto-Protokoll und Übernahmebestätigung. Diskret, versichert bis 25.000 €, ohne Subunternehmer. Auch in Ihrer Abwesenheit möglich, wenn Sie ${ortIn} nicht anwesend sein können.`,
    );
  }




  // === Lokaler Kontext ===
  const wienAnbindung = distanzWien[location.bundeslandSlug] ?? "";
  let localContext = "";
  if (location.type === "bundesland") {
    localContext = `Als Schober Transportservice betreuen wir ganz ${location.name} – von der Landeshauptstadt bis in die kleineren Gemeinden. ${wienAnbindung ? `${location.name} liegt ${wienAnbindung} und ist für unsere Teams gut erreichbar. ` : ""}Wir kennen die regionalen Besonderheiten, die behördlichen Vorgaben bei Halteverbotszonen und die Wege vor Ort. Egal ob Sie ${ortIn} einen Umzug, eine Entrümpelung oder eine Wohnungsauflösung beauftragen möchten – Sie haben mit uns einen einzigen Ansprechpartner für ganz ${location.name}.`;
  } else if (location.type === "wien-bezirk") {
    const bez = location.name.match(/(\d+)\. Bezirk/)?.[1];
    const bauten = meta?.bauten ?? "Mischbebauung aus Altbau und Neubau";
    const parking = meta?.parking ?? "Parkpickerl-Zone – eine Halteverbots-Beantragung ist meist Pflicht";
    const anfahrt = meta?.anfahrt ?? "übliche Wiener Innerstädtische Anfahrt";
    localContext = `Der ${bez}. Wiener Gemeindebezirk${plzInfo} bringt eigene Herausforderungen für ${service.noun} mit: ${bauten}. ${parking}. Unsere Erfahrung mit Anfahrt und Anlieferung: ${anfahrt}. Unser Team kennt den Bezirk seit Jahren und plant Anfahrt, Möbellift, Halteverbot und Abtransport exakt nach den Vorgaben der MA 46 und der jeweiligen Hausverwaltung. Wir schützen Holzböden, Stuckdecken und Stiegenhausgeländer mit Decken und Kantenschutz.`;
  } else {
    const region = location.region ? `${location.region}, ${bundesland}` : bundesland;
    const popText = location.population
      ? `Mit rund ${location.population.toLocaleString("de-AT")} Einwohnern `
      : "";
    const baseText = `${location.name} liegt in ${region}${plzInfo}. ${popText}ist ${location.name} ein wichtiger Standort, an dem wir regelmäßig Aufträge abwickeln.`;
    const tierExtra = meta
      ? ` ${location.name} ist ${meta.charakter ?? "ein bedeutender regionaler Standort"}. Für ${service.noun} relevant: ${meta.bauten ?? "die ortsübliche Bausubstanz"}. ${meta.parking ?? "Die Parkraumbewirtschaftung berücksichtigen wir bei der Auftragsplanung."} ${meta.anfahrt ? `Anfahrt: ${meta.anfahrt}.` : ""}`
      : ` Wir kennen die örtliche Hausverwaltung-Praxis, die regionalen Gegebenheiten und kommen mit eigenem Fuhrpark direkt vor Ort.`;
    localContext = baseText + tierExtra;
  }

  // === Neighborhood Section ===
  let neighborhoodSection: string | undefined;
  if (meta?.stadtteile && meta.stadtteile.length > 0) {
    neighborhoodSection = `Wir sind ${ortIn} in allen Stadtteilen und Lagen aktiv – darunter ${meta.stadtteile.join(", ")}. Für ${service.noun} ${ortIn} ist die exakte Lage entscheidend: Stiegenhaus, Lift, Zufahrt und Parkmöglichkeit bestimmen den Aufwand und damit auch das Angebot. Bei der Besichtigung erfassen wir diese Faktoren systematisch und kalkulieren transparent.`;
  }

  // === Lokale Tipps ===
  const tipsCommon = [
    `Wohnungsübergabe-Protokoll: Wir dokumentieren den Endzustand fotografisch – nützlich für Ihre Hausverwaltung ${ortIn}.`,
    `Faire Preisgestaltung: Bei gut erhaltenem Inventar berücksichtigen wir das im Angebot Ihrer ${service.noun} ${ortIn}.`,
    `Termin-Empfehlung: Wochenmitte (Di–Do) ist preislich oft günstiger als Wochenenden oder Monatsende.`,
    `Heikle Stücke separat: Farben, Lacke, Batterien und Elektronik werden getrennt verladen und an befugte Betriebe übergeben.`,
    `Versicherung prüfen: Standardmäßig versichert bis 25.000 €. Für hochwertige Gegenstände erweitern wir die Deckung auf Wunsch.`,
    `Schlüsselübergabe in Ihrer Abwesenheit: Auf Wunsch übernehmen wir Schlüssel von der Hausverwaltung und übergeben besenrein zurück.`,
  ];
  const tipsLocal: string[] = [];
  if (location.type === "wien-bezirk" || location.bundeslandSlug === "wien") {
    tipsLocal.push(
      `Halteverbot in Wien: Beantragung über die MA 46 dauert in der Regel 14 Tage – planen Sie früh, wir übernehmen die Antragstellung.`,
      `Parkpickerl-Zonen: In allen 23 Wiener Bezirken gilt das ganztägige Parkpickerl. Ohne Halteverbot riskieren Sie Strafmandat und Verzögerung.`,
    );
  }
  if (meta?.parking) tipsLocal.push(`Lokal speziell: ${meta.parking}.`);
  if (meta?.anfahrt) tipsLocal.push(`Anfahrt: ${meta.anfahrt}.`);

  const localTips = pickN([...tipsLocal, ...tipsCommon], key + "tips", tier === 1 ? 6 : tier === 2 ? 5 : 4);

  // === Fallbeispiele ===
  const examplesByService: Record<string, { title: string; text: string }[]> = {
    entruempelung: [
      { title: "3-Zimmer-Altbau", text: `Komplette Entrümpelung einer 78 m² großen Altbauwohnung ${ortIn} nach Erbschaft. 14 m³ Hausrat, Möbel und Sperrgut. Faire Preisgestaltung bei gut erhaltenem Inventar. Abgewickelt in 1,5 Tagen, besenrein übergeben.` },
      { title: "Kellerraum 18 m²", text: `Entrümpelung Kellerabteil ${ortIn} mit Altmöbeln, Reifen und Elektroschrott. 4 m³ Volumen, sortiert nach brauchbaren Stücken und Sperrgut. Abgewickelt an einem Vormittag.` },
      { title: "Einfamilienhaus", text: `Vollständige Hausentrümpelung ${ortIn} vor Verkauf, ca. 32 m³ inkl. Dachboden, Garage und Garten-Werkzeug. 3 Tage Arbeit mit 4-köpfigem Team, faire Preisgestaltung bei gut erhaltenem Inventar.` },
    ],
    "messi-wohnung": [
      { title: "Diskrete Messie-Räumung", text: `Räumung einer stark verwahrlosten 55 m² Wohnung ${ortIn}. Anfahrt mit unbeschriftetem Transporter, Schleuse im Stiegenhaus, PSA-Vollschutz. Sicherung persönlicher Dokumente, anschließende Geruchsneutralisation und Übergabe an Hausverwaltung.` },
      { title: "Räumung mit Schädlingsbefall", text: `Messie-Räumung ${ortIn} mit Schädlingsbefall. Vorab Kammerjäger koordiniert, danach komplette Räumung mit Vollschutz, Desinfektion und Grundreinigung. Wohnung übergabefertig in 3 Tagen.` },
    ],
    umzug: [
      { title: "2-Zimmer-Umzug innerhalb Wiens", text: `Umzug einer 65 m² Wohnung ${ortIn} mit Möbellift (Altbau, kein Personenlift). Verpackung, Demontage, Transport, Wiederaufbau – alles in 1 Tag, Angebot 1.290 €.` },
      { title: "Familien-Umzug mit Klavier", text: `Komplettumzug eines Einfamilienhauses ${ortIn} inkl. Klaviertransport (Pianino). 2 Tage, 5-köpfiges Team, Halteverbotszone an beiden Adressen organisiert.` },
      { title: "Senioren-Umzug ins betreute Wohnen", text: `Behutsamer Umzug ${ortIn} ins betreute Wohnen mit Reduktion des Hausstands. Wir sortierten mit der Familie, übersiedelten die ausgewählten Stücke und entrümpelten den Rest.` },
    ],
    transport: [
      { title: "Same-Day-Transport", text: `Möbeltransport ${ortIn} am gleichen Tag: Sofa und Schrank vom Verkäufer abgeholt und in die Wohnung des Käufers gebracht. 2 Mann, 2 Stunden.` },
      { title: "IKEA-Gesamtkauf", text: `Abholung einer kompletten Wohnungseinrichtung bei IKEA Vösendorf und Anlieferung ${ortIn} inklusive Tragehilfe in den 3. Stock ohne Lift.` },
      { title: "Klaviertransport", text: `Transport eines Flügels ${ortIn} mit Klaviertrolley, Hebebühne und 3-Mann-Team. Sichere Verladung und Anlieferung in den 1. Stock.` },
    ],
    wohnungsaufloesung: [
      { title: "Auflösung im Trauerfall", text: `Komplette Wohnungsauflösung ${ortIn} im Trauerfall: Sichtung mit Angehörigen, Sicherung Dokumente und Erinnerungsstücke, faire Preisgestaltung, besenreine Übergabe an Hausverwaltung in 2 Tagen.` },
      { title: "Auflösung bei Heimübersiedlung", text: `Reduktion und Auflösung einer 80 m² Wohnung ${ortIn} bei Übersiedlung ins Pflegeheim. Begleitete Sortierung, behutsame Abwicklung über 3 Tage.` },
      { title: "Wohnungsauflösung in Abwesenheit", text: `Komplette Abwicklung ${ortIn} mit Schlüsselübernahme von der Hausverwaltung. Foto-Dokumentation, faire Preisgestaltung, besenreine Rückgabe – Auftraggeber im Ausland.` },
    ],
    firmenumzug: [
      { title: "Werkstattübersiedlung Freitag–Sonntag", text: `Umzug einer KFZ-Werkstatt ${ortIn} über das Wochenende. Demontage Hebebühnen, Verladung Werkzeug- und Reifenlager, Wiederaufbau – Montag um 7 Uhr wieder einsatzbereit.` },
      { title: "Lager- und Produktionsverlagerung", text: `Verlagerung eines Kleinlagers (180 Palettenstellplätze) ${ortIn}. Etikettiertes Umzugskonzept, 3-Tage-Plan, keine Lieferausfälle.` },
    ],
    bueroumzug: [
      { title: "Büroumzug 35 Arbeitsplätze", text: `Übersiedlung eines IT-Unternehmens ${ortIn} mit 35 Arbeitsplätzen, Server-Raum und Konferenzräumen. Etiketten-System, DSGVO-konformer Aktentransport, Wochenend-Abwicklung.` },
      { title: "Anwaltskanzlei mit Aktenarchiv", text: `Umzug einer Kanzlei ${ortIn} mit 12 Arbeitsplätzen und 80 lfm Aktenarchiv in versiegelten, nummerierten Boxen. Komplette Inventur und Übergabeprotokoll.` },
    ],
  };

  const exampleCases = pickN(
    examplesByService[service.slug] ?? examplesByService.entruempelung,
    key + "ex",
    tier === 1 ? 3 : 2,
  );

  // === Preis-Tabelle ===
  const priceTable = adjustedPriceTable(service.slug, meta?.preisFaktor ?? 1.0);
  const priceNote =
    meta?.preisFaktor && meta.preisFaktor > 1.05
      ? `Hinweis: ${ortIn} bewegen sich Preise leicht über dem österreichischen Durchschnitt – wegen schwieriger Anfahrt, häufiger Halteverbotszonen und kleinteiliger Anlieferungen. Den endgültigen Angebot erhalten Sie nach kostenloser Besichtigung.`
      : meta?.preisFaktor && meta.preisFaktor < 0.95
        ? `Hinweis: ${ortIn} sind die Konditionen günstiger als in Wien-Innenstadtlage. Wir kalkulieren transparent vor Ort.`
        : `Die Tabellenwerte sind Richtwerte. Den exakten Angebot erhalten Sie nach unverbindlicher Besichtigung – schriftlich, ohne versteckte Kosten.`;

  // === Why Us ===
  const whyAll = [
    `Über 10 Jahre Erfahrung mit ${service.noun} in ganz Österreich`,
    `Preistransparenz – keine Überraschungen auf der Rechnung`,
    `Versicherter Auftrag bis 25.000 €`,
    `Eigenes geschultes Team – keine Subunternehmer`,
    `Termin oft binnen 48 Stunden möglich`,
    `Räumung und Abtransport aus einer Hand`,
    `Persönlicher Ansprechpartner während der gesamten Abwicklung`,
    `Diskrete Abwicklung – auf Wunsch ohne Werbung am Fahrzeug`,
    `Faire, ortsübliche Preise – auch ${ortIn}`,
    `Schriftliches Angebot in 24 Stunden`,
  ];
  const whyUs = pickN(whyAll, key + "why", tier === 1 ? 6 : 5);

  // === FAQ ===
  const faq = buildFaq(service, location, ortIn, plzInfo, key, tier);

  return {
    title: pick(titleVariants, key, "t").slice(0, 58),
    h1: pick(h1Variants, key, "h"),
    metaDescription: pick(metaVariants, key, "m").slice(0, 158),
    ogTitle: pick(titleVariants, key, "og").replace(" | Schober Transportservice", "").replace(" | Schober", ""),
    ogDescription: pick(metaVariants, key, "ogm").slice(0, 200),
    intro: pick(introVariants, key, "i"),
    introExtra: pick(introExtraVariants, key, "i2"),
    localContext: nameVariants[location.slug]
      ? `${localContext} Unser Einsatzgebiet umfasst ${nameVariants[location.slug].join(", ")} – ${service.title} ${ortBare} übernehmen wir direkt von unserem Standort in Bad Fischau-Brunn aus.`
      : localContext,
    neighborhoodSection,
    localTips,
    exampleCases,
    priceTable,
    priceNote,
    whyUs,
    faq,
    tier,
    noindex: !isFocusLocation(location),
  };
}

function buildFaq(
  service: LocalService,
  location: Location,
  ortIn: string,
  plzInfo: string,
  key: string,
  tier: Tier,
): { question: string; answer: string }[] {
  const common = [
    {
      question: `Was kostet eine ${service.title} ${ortIn}?`,
      answer: `Der Preis für eine ${service.noun} ${ortIn}${plzInfo} hängt vom Volumen, Aufwand und der Erreichbarkeit ab. Nach einer kostenlosen Besichtigung oder Übermittlung von Fotos erhalten Sie ein verbindliches Angebot – ohne versteckte Kosten. Richtwerte für ${location.bundesland}: kleine Wohnung ab rund 590 €, Einfamilienhaus ab rund 2.490 €. Rufen Sie uns unter +43 680 230 87 00 an.`,
    },
    {
      question: `Wie schnell ist ein Termin ${ortIn} möglich?`,
      answer: `In den meisten Fällen können wir einen Termin für ${service.noun} ${ortIn} innerhalb von 48 Stunden anbieten. Bei Notfällen wie kurzfristiger Wohnungsübergabe oder Trauerfall sind auch Same-Day-Termine möglich. Kontaktieren Sie uns möglichst frühzeitig – besonders zum Monatsende sind Termine begehrt.`,
    },
    {
      question: `Was passiert mit dem Räumgut nach dem Abtransport?`,
      answer: `Wir räumen Ihr Objekt und transportieren das Räumgut ab. Die weitere Behandlung und Verwertung erfolgt ausschließlich über hierzu befugte Betriebe in ${location.bundesland}. Auf Wunsch erhalten Sie eine Übernahmebestätigung für Ihre Unterlagen.`,
    },
    {
      question: `Wird gut erhaltenes Inventar im Preis berücksichtigt?`,
      answer: `Ja. Bei gut erhaltenem Inventar gestalten wir den Preis Ihrer ${service.title} ${ortIn} entsprechend fair. Die Kalkulation ist transparent und nachvollziehbar – ohne versteckte Positionen.`,
    },
    {
      question: `Ist die Leistung versichert?`,
      answer: `Ja, jeder Auftrag von Schober Transportservice ist standardmäßig mit bis zu 25.000 € haftpflichtversichert. Auf Wunsch lässt sich die Deckung für hochwertige Gegenstände erhöhen. Unser Team ist geschult und arbeitet mit professioneller Schutzausrüstung.`,
    },
    {
      question: `Übernehmen Sie auch die Endreinigung ${ortIn}?`,
      answer: `Auf Wunsch erfolgt eine besenreine Übergabe inklusive Grundreinigung. Für eine vollständige Endreinigung mit Fenstern und Sanitärbereich kooperieren wir mit professionellen Reinigungsfirmen ${ortIn} – die Kosten weisen wir transparent im Angebot aus.`,
    },
    {
      question: `Wie läuft die Besichtigung ab?`,
      answer: `Die Besichtigung ${ortIn} ist kostenlos und unverbindlich. Wir erfassen Volumen, Bausubstanz (Stockwerk, Lift, Stiegenhaus), Zufahrtssituation und Wertgegenstände. Innerhalb von 24 Stunden erhalten Sie das schriftliche Angebot per E-Mail.`,
    },
    {
      question: `Können Sie auch ohne meine Anwesenheit arbeiten?`,
      answer: `Ja. Wir können den Auftrag ${ortIn}${plzInfo} bei Schlüsselübergabe komplett in Ihrer Abwesenheit abwickeln – inklusive Foto-Dokumentation und besenreiner Übergabe an Hausverwaltung oder Eigentümer. Diese Variante nutzen viele Auftraggeber, die im Ausland leben.`,
    },
    {
      question: `Welche Zahlungsmöglichkeiten bieten Sie?`,
      answer: `Wir akzeptieren Banküberweisung mit Erlagschein, Barzahlung bei kleineren Aufträgen und für Firmenkunden Rechnungslegung mit 14 Tagen Zahlungsziel. Bei größeren Aufträgen ist eine Anzahlung möglich – mehr als die Hälfte erst nach Auftragsabschluss.`,
    },
    {
      question: `Arbeiten Sie auch am Wochenende ${ortIn}?`,
      answer: `Ja. Auf Wunsch arbeiten wir Samstag und Sonntag – für Firmenumzüge ist das die Standard-Konstellation, um Ausfallzeiten zu vermeiden. Bei Privatkunden fällt ein moderater Wochenend-Zuschlag an.`,
    },
  ];

  const serviceSpecific: Record<string, { question: string; answer: string }[]> = {
    "messi-wohnung": [
      {
        question: `Arbeiten Sie diskret – auch ${ortIn}?`,
        answer: `Absolut. Bei Messie-Räumungen ${ortIn} kommen wir auf Wunsch mit neutralen Fahrzeugen ohne Firmenwerbung. Wir koordinieren die Anlieferung so, dass möglichst wenig öffentliche Aufmerksamkeit entsteht, und kommunizieren diskret mit der Hausverwaltung. Das ist seit Jahren unser Standard und wird von Angehörigen besonders geschätzt.`,
      },
      {
        question: `Was ist mit Schädlingsbefall oder Geruch ${ortIn}?`,
        answer: `Wir koordinieren auf Wunsch zertifizierte Kammerjäger und führen anschließend Desinfektion und Geruchsneutralisation durch. So wird das Objekt ${ortIn} sicher übergabefertig. Bei starkem Befall arbeiten wir mit Ozon-Geruchsbehandlung – das Objekt ist nach 24 Stunden wieder neutral.`,
      },
      {
        question: `Wer trägt die Kosten bei Messie-Räumung im Mietverhältnis?`,
        answer: `Üblicherweise der Mieter bzw. die Erben. Bei Räumungs- und Insolvenzfällen tragen die Kosten Hausverwaltung oder Insolvenzverwalter. Wir stellen die Rechnung auf den jeweiligen Auftraggeber aus und unterstützen Sie bei der Klärung mit der Hausverwaltung ${ortIn}.`,
      },
    ],
    entruempelung: [
      {
        question: `Auch Kellerentrümpelung oder Dachboden ${ortIn}?`,
        answer: `Ja. Wir entrümpeln Keller, Dachböden, Garagen und Lagerflächen ${ortIn}${plzInfo}. Auch Einzelstücke wie alte Sofas, Küchengeräte oder Werkbänke werden auf Wunsch abgeholt und abtransportiert. Preisrichtwert für ein Kellerabteil: ab 290 €.`,
      },
      {
        question: `Brauche ich für die Entrümpelung eine Halteverbotszone?`,
        answer: `Häufig ja – besonders in Altstadtlagen oder Bezirken mit Parkpickerl. Wir beantragen die Halteverbotszone für Sie bei der zuständigen Behörde (in Wien bei der MA 46), die Kosten werden im Angebot ausgewiesen.`,
      },
    ],
    umzug: [
      {
        question: `Brauche ich für meinen Umzug ${ortIn} eine Halteverbotszone?`,
        answer: `Bei beengten Zufahrten – häufig in Altstadtbereichen oder Bezirken mit Parkpickerl – ja. Wir organisieren die Halteverbotszone und beantragen sie bei der zuständigen Behörde. Die Kosten (ca. 90–150 €) werden im Angebot ausgewiesen. Vorlaufzeit in Wien: 14 Tage.`,
      },
      {
        question: `Können Sie auch Klavier und Tresor übersiedeln?`,
        answer: `Ja. Für Klaviertransport und schwere Tresore verwenden wir Spezialgeräte und einen geschulten Klaviertransport-Trolley. ${ortIn} sind wir damit häufig im Einsatz – auch Flügel oder schwere Geldschränke ab 500 kg.`,
      },
      {
        question: `Verpacken Sie auch selbst?`,
        answer: `Ja, auf Wunsch übernehmen wir die komplette Verpackung mit Umzugskartons, Luftpolsterfolie und Spezialdecken für Möbel. Sie können auch nur einzelne Bereiche – etwa Küche oder Geschirr – verpacken lassen.`,
      },
    ],
    transport: [
      {
        question: `Sind kurzfristige Transporte ${ortIn} möglich?`,
        answer: `Ja. Sofern unser Fuhrpark verfügbar ist, sind sogar Same-Day-Transporte ${ortIn} möglich. Rufen Sie uns am besten direkt unter +43 680 230 87 00 an – wir prüfen sofort, ob ein Wagen frei ist.`,
      },
      {
        question: `Was kostet die Anfahrt?`,
        answer: `Im Einsatzgebiet rund um ${location.name} ist die Anfahrt im Stundensatz inkludiert. Bei weiteren Strecken berechnen wir eine transparente Kilometerpauschale, die im Angebot ausgewiesen ist.`,
      },
    ],
    wohnungsaufloesung: [
      {
        question: `Sie wickeln auch im Trauerfall einfühlsam ab?`,
        answer: `Ja. Eine Wohnungsauflösung im Trauerfall ${ortIn} erfordert besondere Sensibilität. Wir geben Ihnen Zeit, persönliche Erinnerungsstücke zu sichern, und übernehmen den Rest – auch in Abwesenheit der Angehörigen. Dokumente, Fotos und Schmuck sichern wir separat und übergeben sie persönlich.`,
      },
      {
        question: `Können Sie ohne meine Anwesenheit auflösen?`,
        answer: `Ja. Wir können die Wohnungsauflösung ${ortIn}${plzInfo} bei Schlüsselübergabe auch komplett in Ihrer Abwesenheit durchführen und übergeben anschließend besenrein an Hausverwaltung oder Eigentümer. Sie erhalten eine Foto-Dokumentation des Endzustands.`,
      },
      {
        question: `Was passiert mit gefundenen Wertgegenständen?`,
        answer: `Wertgegenstände – Bargeld, Schmuck, Sparbücher, Dokumente – werden separat erfasst, fotografiert und Ihnen persönlich übergeben oder zugeschickt. Wir arbeiten mit klaren Protokollen, damit nichts verlorengeht.`,
      },
    ],
    firmenumzug: [
      {
        question: `Können Sie nachts oder am Wochenende übersiedeln?`,
        answer: `Ja. Firmenumzüge ${ortIn} planen wir typischerweise für Freitagabend bis Sonntag, damit Ihr Betrieb am Montag wieder läuft. Auch reine Nachtumzüge sind möglich – etwa für Einzelhandel, Apotheken oder Praxen.`,
      },
      {
        question: `Demontieren Sie auch Maschinen und Regalanlagen?`,
        answer: `Ja, unser Team demontiert Regalsysteme, Werkbänke und kleinere Produktionsmaschinen, transportiert sie sicher und montiert sie am Zielort wieder auf. Bei spezialisierten Anlagen koordinieren wir den Hersteller-Service mit.`,
      },
    ],
    bueroumzug: [
      {
        question: `Wie wird der Akten- und Datentransport ${ortIn} abgesichert?`,
        answer: `Akten transportieren wir in versiegelten, nummerierten Boxen DSGVO-konform. Server und sensible Hardware werden vorab vom Strom genommen, abgesichert verpackt und mit erschütterungsarmem Equipment transportiert. Auf Wunsch erstellen wir ein lückenloses Übergabeprotokoll.`,
      },
      {
        question: `Bekommt jeder Arbeitsplatz ein eigenes Etikett?`,
        answer: `Ja. Jeder Karton, jeder Bildschirm und jedes Möbelstück erhält einen farbcodierten Etiketten-Code mit Zielraum und Sitzplatznummer. Am Zielort steht jeder Arbeitsplatz exakt so wie geplant – Ihre Mitarbeiter müssen am Montag nichts suchen.`,
      },
    ],
  };

  const extras = serviceSpecific[service.slug] ?? [];
  const pool = [...extras, ...common];
  const count = tier === 1 ? 8 : tier === 2 ? 7 : 6;
  const startIdx = hash(key) % pool.length;
  const out: { question: string; answer: string }[] = [];
  const seen = new Set<string>();
  for (let i = 0; out.length < count && i < pool.length; i++) {
    const item = pool[(startIdx + i) % pool.length];
    if (!seen.has(item.question)) {
      seen.add(item.question);
      out.push(item);
    }
  }
  return out;
}
